// Alignment + Distribution (V2 Slice 4).
//
// Operiert auf Y.Map in einer Transaction. Locked-Objekte werden geskippt.
// Cascade: wenn ein Container (block/region) aligned wird, bewegen sich Children
// automatisch ueber den cascade.ts-Pfad — hier wird das delta auf den Container
// angewendet, Children werden separat per `collectCascadeIds` mitgenommen.

import { type CanvasObject, isCanvasObject } from "@ms-whiteboard/shared";
import type * as Y from "yjs";
import { collectCascadeIds, filterUnlocked } from "./cascade.js";
import { objectBounds } from "./object-bounds.js";

export type AlignMode =
  | "left"
  | "center-h"
  | "right"
  | "top"
  | "center-v"
  | "bottom"
  | "distribute-h"
  | "distribute-v";

function getBoundsBox(obj: CanvasObject): {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
} {
  const b = objectBounds(obj);
  return {
    left: obj.x,
    right: obj.x + b.w,
    centerX: obj.x + b.w / 2,
    top: obj.y,
    bottom: obj.y + b.h,
    centerY: obj.y + b.h / 2,
  };
}

export function alignSelection(
  yMap: Y.Map<unknown>,
  selection: Iterable<string>,
  mode: AlignMode,
): void {
  const ids = Array.from(selection);
  if (ids.length < 2) return;
  const objects: CanvasObject[] = [];
  for (const id of ids) {
    const raw = yMap.get(id);
    if (isCanvasObject(raw)) objects.push(raw);
  }
  if (objects.length < 2) return;

  const deltas = new Map<string, { dx: number; dy: number }>();

  if (mode === "distribute-h" || mode === "distribute-v") {
    computeDistributeDeltas(objects, mode, deltas);
  } else {
    const boxes = objects.map((o) => ({ obj: o, box: getBoundsBox(o) }));
    let target = 0;
    if (mode === "left") target = Math.min(...boxes.map((b) => b.box.left));
    else if (mode === "right") target = Math.max(...boxes.map((b) => b.box.right));
    else if (mode === "center-h") {
      const minL = Math.min(...boxes.map((b) => b.box.left));
      const maxR = Math.max(...boxes.map((b) => b.box.right));
      target = (minL + maxR) / 2;
    } else if (mode === "top") target = Math.min(...boxes.map((b) => b.box.top));
    else if (mode === "bottom") target = Math.max(...boxes.map((b) => b.box.bottom));
    else if (mode === "center-v") {
      const minT = Math.min(...boxes.map((b) => b.box.top));
      const maxB = Math.max(...boxes.map((b) => b.box.bottom));
      target = (minT + maxB) / 2;
    }
    for (const { obj, box } of boxes) {
      let dx = 0;
      let dy = 0;
      switch (mode) {
        case "left":
          dx = target - box.left;
          break;
        case "right":
          dx = target - box.right;
          break;
        case "center-h":
          dx = target - box.centerX;
          break;
        case "top":
          dy = target - box.top;
          break;
        case "bottom":
          dy = target - box.bottom;
          break;
        case "center-v":
          dy = target - box.centerY;
          break;
      }
      if (dx !== 0 || dy !== 0) deltas.set(obj.id, { dx, dy });
    }
  }

  if (deltas.size === 0) return;

  // Cascade: aligned Container nehmen Children mit.
  const cascade = collectCascadeIds(yMap, deltas.keys());
  const moveIds = filterUnlocked(yMap, cascade);

  yMap.doc?.transact(() => {
    for (const id of moveIds) {
      const raw = yMap.get(id);
      if (!isCanvasObject(raw)) continue;
      // Direkt-aligned: das delta aus deltas.
      // Indirekt-aligned (Child): das delta des _direkten_ Parents anwenden.
      const directDelta = deltas.get(id);
      if (directDelta) {
        yMap.set(id, { ...raw, x: raw.x + directDelta.dx, y: raw.y + directDelta.dy });
        continue;
      }
      // Kind eines aligned Containers — finde die Delta-Source.
      const parentDelta = findParentDelta(yMap, raw, deltas);
      if (parentDelta) {
        yMap.set(id, { ...raw, x: raw.x + parentDelta.dx, y: raw.y + parentDelta.dy });
      }
    }
  });
}

function findParentDelta(
  yMap: Y.Map<unknown>,
  obj: CanvasObject,
  deltas: Map<string, { dx: number; dy: number }>,
): { dx: number; dy: number } | null {
  if (obj.parentRegionId) {
    const direct = deltas.get(obj.parentRegionId);
    if (direct) return direct;
    // Region-Parent ist nicht direkt aligned — ist der Block aligned?
    const region = yMap.get(obj.parentRegionId);
    if (isCanvasObject(region) && region.type === "region") {
      const blockDelta = deltas.get(region.blockId);
      if (blockDelta) return blockDelta;
    }
  }
  if (obj.type === "region") {
    const blockDelta = deltas.get(obj.blockId);
    if (blockDelta) return blockDelta;
  }
  return null;
}

function computeDistributeDeltas(
  objects: readonly CanvasObject[],
  mode: "distribute-h" | "distribute-v",
  out: Map<string, { dx: number; dy: number }>,
): void {
  if (objects.length < 3) return;
  const horizontal = mode === "distribute-h";
  const sorted = [...objects].sort((a, b) => {
    if (horizontal) return getBoundsBox(a).left - getBoundsBox(b).left;
    return getBoundsBox(a).top - getBoundsBox(b).top;
  });
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return;
  const firstBox = getBoundsBox(first);
  const lastBox = getBoundsBox(last);
  const totalSpan = horizontal
    ? lastBox.centerX - firstBox.centerX
    : lastBox.centerY - firstBox.centerY;
  const step = totalSpan / (sorted.length - 1);
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const item = sorted[i];
    if (!item) continue;
    const itemBox = getBoundsBox(item);
    const targetCenter = horizontal ? firstBox.centerX + step * i : firstBox.centerY + step * i;
    if (horizontal) {
      const dx = targetCenter - itemBox.centerX;
      if (dx !== 0) out.set(item.id, { dx, dy: 0 });
    } else {
      const dy = targetCenter - itemBox.centerY;
      if (dy !== 0) out.set(item.id, { dx: 0, dy });
    }
  }
}
